import dayjs from 'dayjs';
import { isNil } from 'lodash';

import { SHORT_MONTH_DAY_YEAR_FORMAT } from 'constants/date';

import {
  MARKET_RATES_COMPLIANCE_NOTE,
  MARKET_RATES_HERO_LABEL,
  MARKET_RATES_PMMS_SOURCE,
} from './marketRates.constants';

// PMMS 30yr survey points → an ascending-by-date sparkline series for the inline
// trend. Surveys arrive newest-first or oldest-first depending on the backend, so
// we sort by surveyDate to guarantee a left-to-right chronological trend.
const buildSparklinePoints = surveys =>
  [...surveys]
    .filter(survey => !isNil(survey?.interest30Years))
    .sort((left, right) => dayjs(left.surveyDate).valueOf() - dayjs(right.surveyDate).valueOf())
    .map(survey => survey.interest30Years);

const composeHeroValue = deltaBps => {
  if (isNil(deltaBps)) {
    return '—';
  }

  const sign = deltaBps > 0 ? '+' : '';

  return `${sign}${deltaBps} bps`;
};

const composeRateSheetStatus = rateSheet => {
  if (isNil(rateSheet)) {
    return null;
  }

  const publishedOn = isNil(rateSheet.dateUploaded)
    ? null
    : dayjs(rateSheet.dateUploaded).format(SHORT_MONTH_DAY_YEAR_FORMAT);
  const repriceFragment = rateSheet.repricesToday ? ' · reprices today' : '';

  return [rateSheet.title, publishedOn ? `published ${publishedOn}` : null]
    .filter(Boolean)
    .join(' · ')
    .concat(repriceFragment);
};

/**
 * Maps the PMMS benchmark + latest rate sheet into the RATES pillar display shape.
 * Returns null when neither responded so the reducer holds its empty state.
 * @param {object|null} pmms - { surveys, movement, latestSurveyDate, source, ... }
 * @param {object|null} rateSheet - { rateSheetId, channelId, title, dateUploaded, repricesToday }
 * @returns {object|null}
 */
export const mapMarketRates = ({ pmms, rateSheet }) => {
  if (isNil(pmms) && isNil(rateSheet)) {
    return null;
  }

  const deltaBps = pmms?.movement?.['30YearsDeltaBps'] ?? null;
  const surveyDate = pmms?.latestSurveyDate ?? pmms?.movement?.currentSurveyDate ?? null;

  return {
    heroValue: composeHeroValue(deltaBps),
    heroLabel: MARKET_RATES_HERO_LABEL,
    deltaBps,
    sparklinePoints: buildSparklinePoints(pmms?.surveys ?? []),
    rateSheetStatus: composeRateSheetStatus(rateSheet),
    sourceLabel: pmms?.source ?? MARKET_RATES_PMMS_SOURCE,
    surveyDateLabel: isNil(surveyDate)
      ? null
      : dayjs(surveyDate).format(SHORT_MONTH_DAY_YEAR_FORMAT),
    complianceNote: pmms?.complianceNote ?? MARKET_RATES_COMPLIANCE_NOTE,
  };
};
